import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, TrendingDown, CheckCircle, AlertCircle, Minus, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * FeedbackStats Component
 * Shows user feedback statistics for prediction accuracy with trending
 */
const FeedbackStats = ({ airportCode, className = '' }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!airportCode) {
      setStats(null);
      return;
    }

    const fetchStats = async () => {
      setLoading(true);
      try {
        // Fetch airport-wide stats (not filtered by crowd level)
        const url = `${API_URL}/api/airport/feedback/stats?airport_code=${airportCode}`;
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (err) {
        console.error('Failed to fetch feedback stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [airportCode]);

  // Don't render if no stats or loading
  if (loading || !stats) return null;
  
  // Don't render if no feedback yet
  const totalReports = stats.total_feedback || stats.user_reports || 0;
  if (totalReports === 0) return null;

  const accuracyRate = stats.accuracy_rate || 0;
  const breakdown = stats.breakdown || {};
  const trend = stats.trend;
  const trendDescription = stats.trend_description;

  // Determine icon and color based on accuracy
  const getAccuracyDisplay = () => {
    if (totalReports < 5) {
      return {
        icon: Users,
        color: 'text-slate-500',
        bgColor: 'bg-slate-50',
        label: `${totalReports} user report${totalReports > 1 ? 's' : ''}`
      };
    }
    
    if (accuracyRate >= 80) {
      return {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        label: `${accuracyRate}% accurate`
      };
    } else if (accuracyRate >= 60) {
      return {
        icon: Users,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        label: `${totalReports} reports`
      };
    } else {
      return {
        icon: Sparkles,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        label: 'Learning...'
      };
    }
  };

  // Get trend icon and color
  const getTrendDisplay = () => {
    if (!trend || totalReports < 5) return null;
    
    switch (trend) {
      case 'improving':
        return {
          icon: TrendingUp,
          color: 'text-green-600',
          bgColor: 'bg-green-100'
        };
      case 'declining':
        return {
          icon: TrendingDown,
          color: 'text-orange-600',
          bgColor: 'bg-orange-100'
        };
      case 'stable':
        return {
          icon: Minus,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100'
        };
      default:
        return null;
    }
  };

  const display = getAccuracyDisplay();
  const trendDisplay = getTrendDisplay();
  const Icon = display.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`${className}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {/* Main accuracy badge */}
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${display.bgColor}`}>
          <Icon className={`w-4 h-4 ${display.color}`} />
          <span className={`text-sm font-medium ${display.color}`}>
            {display.label}
          </span>
        </div>
        
        {/* Trend badge */}
        {trendDisplay && trendDescription && (
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${trendDisplay.bgColor}`}>
            <trendDisplay.icon className={`w-3.5 h-3.5 ${trendDisplay.color}`} />
            <span className={`text-xs font-medium ${trendDisplay.color}`}>
              {trendDescription}
            </span>
          </div>
        )}
      </div>

      {/* Detailed breakdown */}
      {totalReports >= 5 && (
        <div className="mt-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1 mr-3">
            <span className="w-2 h-2 rounded-full bg-green-400"></span>
            {breakdown.as_expected || 0} accurate
          </span>
          <span className="inline-flex items-center gap-1 mr-3">
            <span className="w-2 h-2 rounded-full bg-blue-400"></span>
            {breakdown.less_busy || 0} said quieter
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-orange-400"></span>
            {breakdown.more_busy || 0} said busier
          </span>
        </div>
      )}
    </motion.div>
  );
};

export default FeedbackStats;
